import { ChatMessageRole, ComplianceCheck, JobStatus, UserRole } from '@avanzu/types';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useChatGenerate } from '../../contexts/QueueContext';
import { streamChat } from '../../services/chat';
import { dispatchProjectGeneration } from '../../services/projectGeneration';

export const ProjectChatPageRoles = [UserRole.AVANZU, UserRole.OWNER, UserRole.ADMIN];

interface Message {
  role: ChatMessageRole;
  content: string;
  timestamp: number;
}

export const ProjectChat: React.FC = () => {
  const { organisationId } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { startGeneration, currentJob } = useChatGenerate();

  const [messages, setMessages] = useState<Message[]>([
    {
      role: ChatMessageRole.ASSISTANT,
      content:
        "Hello! I'm your Avanzu R&D consultant. I'll help you create an R&D project plan through our conversation.\n\nLet's start with the basics: What technical problem or innovation is your team working on?",
      timestamp: Date.now(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [complianceChecks, setComplianceChecks] = useState<ComplianceCheck[]>([
    { id: 'unknown_outcome', label: 'Unknown Outcome / Technical Uncertainty', completed: false },
    {
      id: 'systematic_progression',
      label: 'Systematic Progression / Scientific Method',
      completed: false,
    },
    { id: 'new_knowledge', label: 'Purpose to Generate New Knowledge', completed: false },
    { id: 'context_complete', label: 'Project Context & Details', completed: false },
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleComplianceUpdate = (progress: any) => {
    setComplianceChecks([
      {
        id: 'unknown_outcome',
        label: 'Unknown Outcome / Technical Uncertainty',
        completed: progress.unknown_outcome,
      },
      {
        id: 'systematic_progression',
        label: 'Systematic Progression / Scientific Method',
        completed: progress.systematic_progression,
      },
      {
        id: 'new_knowledge',
        label: 'Purpose to Generate New Knowledge',
        completed: progress.new_knowledge,
      },
      {
        id: 'context_complete',
        label: 'Project Context & Details',
        completed: progress.context_complete,
      },
    ]);
  };

  // Check if generation is complete and navigate
  useEffect(() => {
    if (currentJob?.status === JobStatus.COMPLETED && currentJob.projectId) {
      navigate(`/app/projects/${currentJob.projectId}`);
    }
  }, [currentJob, navigate]);

  const handleSend = async () => {
    if (!input.trim() || !organisationId) return;

    const userMessage: Message = {
      role: ChatMessageRole.USER,
      content: input.trim(),
      timestamp: Date.now(),
    };

    // Add user message immediately
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsStreaming(true);

    try {
      // Prepare messages for API (exclude timestamp)
      const apiMessages = [...messages, userMessage].map(({ role, content }) => ({
        role,
        content,
      }));

      let assistantContent = '';
      const assistantMessage: Message = {
        role: ChatMessageRole.ASSISTANT,
        content: '',
        timestamp: Date.now(),
      };

      // Add empty assistant message that we'll update
      setMessages((prev) => [...prev, assistantMessage]);

      // Stream the response
      await streamChat(
        organisationId,
        { messages: apiMessages },
        (chunk: string) => {
          assistantContent += chunk;
          setMessages((prev) => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = {
              ...assistantMessage,
              content: assistantContent,
            };
            return newMessages;
          });
        },
        handleComplianceUpdate
      );
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to send message', {
        variant: 'error',
      });
      // Remove the empty assistant message on error
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleGenerate = async () => {
    if (!organisationId) return;

    try {
      // Dispatch generation job
      const apiMessages = messages.map(({ role, content }) => ({ role, content }));
      const { jobId } = await dispatchProjectGeneration(organisationId, apiMessages);

      // Start tracking in context (which handles polling)
      startGeneration({
        jobId,
        status: JobStatus.QUEUED,
        organisationId,
        pollInterval: 2000,
      });

      enqueueSnackbar('Project generation started!', { variant: 'info' });

      // Navigate away from chat - user can monitor progress in sidebar
      navigate('/app/projects');
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to start project generation',
        { variant: 'error' }
      );
    }
  };

  const isGenerating =
    currentJob?.status === JobStatus.IN_PROGRESS || currentJob?.status === JobStatus.QUEUED;
  const isReadyToGenerate = complianceChecks.every((check) => check.completed);
  const completedCount = complianceChecks.filter((check) => check.completed).length;
  const progressPercentage = (completedCount / complianceChecks.length) * 100;

  return (
    <PaperSection title="Create R&D Project with AI" action={<AutoFixHighIcon />}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          height: { xs: 'auto', md: 'calc(100vh - 250px)' },
        }}
      >
        {/* Chat Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height: { xs: '500px', md: 'auto' },
          }}
        >
          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    maxWidth: '70%',
                    bgcolor: message.role === 'user' ? 'primary.light' : 'background.paper',
                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    '& p': { m: 0, mb: 1 },
                    '& p:last-child': { mb: 0 },
                    '& strong': { fontWeight: 600 },
                    '& em': { fontStyle: 'italic' },
                    '& ul, & ol': { mt: 0.5, mb: 0.5, pl: 2 },
                    '& li': { mb: 0.5 },
                  }}
                >
                  <Typography variant="body1" component="div">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </Typography>
                </Paper>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Type your message... (Shift+Enter for new line)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isStreaming || isGenerating}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!input.trim() || isStreaming || isGenerating}
                sx={{ alignSelf: 'flex-end' }}
              >
                {isStreaming ? <CircularProgress size={24} /> : <SendIcon />}
              </IconButton>
            </Stack>

            <Box
              sx={{
                mt: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Powered by Avanzu AI • {messages.filter((m) => m.role === 'user').length} messages
              </Typography>
              <Button size="small" onClick={() => navigate('/app/projects')}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>

        <Divider
          flexItem
          sx={{
            display: { xs: 'block', md: 'none' },
            ml: 0,
            mt: 3,
            mb: 3,
          }}
        />
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            display: { xs: 'none', md: 'block' },
            ml: 4,
          }}
        />

        {/* Compliance Progress Section */}
        <Box
          sx={{
            width: { xs: '100%', md: 300 },
            display: 'flex',
            ml: { xs: 0, md: 4 },
            flexDirection: 'column',
          }}
        >
          <Typography variant="h6" gutterBottom>
            R&D Project Readiness
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, display: { xs: 'none', md: 'block' } }}
          >
            Track progress through the three core AusIndustry R&D tests
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="caption" fontWeight="bold">
                {completedCount}/{complianceChecks.length}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {/* Compliance Checks */}
          <Stack spacing={2} sx={{ flex: 1, display: { xs: 'none', md: 'flex' } }}>
            {complianceChecks.map((check) => (
              <Box key={check.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={check.completed}
                  disabled
                  sx={{
                    p: 0,
                    '&.Mui-disabled': {
                      color: check.completed ? 'success.main' : 'text.disabled',
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: check.completed ? 'text.primary' : 'text.secondary',
                    fontWeight: check.completed ? 500 : 400,
                  }}
                >
                  {check.label}
                </Typography>
              </Box>
            ))}
          </Stack>

          {/* Generate Button */}
          <Box sx={{ mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              startIcon={<RocketLaunchIcon />}
              onClick={handleGenerate}
              disabled={!isReadyToGenerate || isStreaming || isGenerating}
            >
              Generate Project
            </Button>
            {!isReadyToGenerate && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block', textAlign: 'center' }}
              >
                Complete all checks to enable generation
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </PaperSection>
  );
};
